import { defineComponent as _defineComponent } from 'vue'

import { Popover } from 'element-ui'

const __default__ = {
  name: 'campaign-report',
  components: {
    [Popover.name]: Popover
  }
}

import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { BTable, BPagination } from 'bootstrap-vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { holdStatusLabel, holdHintText } from '@/util/campaignHold'
import { Badge } from 'src/components'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import AttributedPurchasesDrawer from './AttributedPurchasesDrawer.vue'
import { REASON_LABELS, SUPPRESSION_CODES, reasonLabel } from '@/util/campaignSuppressionReasons'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const exporting = ref(false)
const campaign = ref<any>(null)
const totals = ref<any>({
  recipients: 0, sent: 0, failed: 0, skipped: 0,
  delivered: 0, opens: 0, unique_opens: 0, clicks: 0, unique_clicks: 0,
  bounces: 0, complaints: 0, unsubscribes: 0, suppressed: 0
})
const attribution = ref<any>({ model: 'click_7d', available: false, purchases: 0, revenue: 0, customers: 0 })
// Attributed purchases drawer: lazy, fetched on first open, paged server side
const purchasesDrawerOpen = ref(false)
const purchases = ref<any[]>([])
const purchasesTotal = ref(0)
const purchasesLoading = ref(false)
const purchasesPage = ref(1)
const PURCHASES_PER_PAGE = 25
const purchasesExporting = ref(false)
const recipients = ref<any[]>([])
const recipientTotal = ref(0)
const recipientsScoped = ref(false)
const recipientsRefreshing = ref(false)
const currentPage = ref(1)
const perPage = ref(25)
const recipientFilters = ref<Record<string, any>>({ search: '', status: [], issue: [], reason: [] })
let pollTimer: ReturnType<typeof setInterval> | null = null
// 10 s while sending; a held or warming up campaign on an open tab polls once a minute
let pollIntervalMs = 0

const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({ label, value }))

// Deliverability rows download the matching recipients as CSV. Failed,
// Skipped and Suppressed are recipient statuses; Bounces and Complaints are
// event flags, so those files list distinct recipients and can hold fewer rows
// than the event count shown.
const EMPTY_FILTERS = { search: '', status: [] as string[], issue: [] as string[], reason: [] as string[] }
const STAT_DOWNLOADS: Record<string, { filters: Record<string, any>; suffix: string }> = {
  failed: { filters: { ...EMPTY_FILTERS, status: ['failed'] }, suffix: 'failed' },
  skipped: { filters: { ...EMPTY_FILTERS, status: ['skipped'] }, suffix: 'skipped' },
  suppressed: { filters: { ...EMPTY_FILTERS, status: ['skipped'], reason: SUPPRESSION_CODES }, suffix: 'suppressed' },
  bounces: { filters: { ...EMPTY_FILTERS, issue: ['bounced'] }, suffix: 'bounced' },
  complaints: { filters: { ...EMPTY_FILTERS, issue: ['complained'] }, suffix: 'complaints' }
}
function downloadStat(key: string) {
  const def = STAT_DOWNLOADS[key]
  if (def) downloadCsv(def.filters, def.suffix)
}

const recipientFilterSchema = [
  { key: 'search', type: 'text', label: 'Name or email', placeholder: 'Search recipients', primary: true },
  {
    key: 'status',
    type: 'multiselect',
    label: 'Status',
    primary: true,
    options: [
      { label: 'Sent', value: 'sent' },
      { label: 'Failed', value: 'failed' },
      { label: 'Skipped', value: 'skipped' },
      { label: 'Pending', value: 'pending' },
      { label: 'Processing', value: 'processing' }
    ]
  },
  // Post-send problems reported by the mailbox provider; any selected value matches
  {
    key: 'issue',
    type: 'multiselect',
    label: 'Delivery issue',
    primary: true,
    options: [
      { label: 'Bounced', value: 'bounced' },
      { label: 'Marked as spam', value: 'complained' }
    ]
  },
  { key: 'reason', type: 'multiselect', label: 'Reason', primary: true, options: REASON_OPTIONS }
]

const storedPermissions = (() => {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '[]') as string[]
  } catch {
    return []
  }
})()
const hasDownloadPermission = storedPermissions.includes('download_data')
const hasCustomerViewPermission = storedPermissions.includes('customer_view')

// The download slot beside each Deliverability number keeps the numbers in
// one column; it is only rendered when at least one row has a download, so a
// card with nothing to download keeps its numbers flush with the other cards
const hasAnyStatDownload = computed(() => hasDownloadPermission
  && ['failed', 'skipped', 'suppressed', 'bounces', 'complaints'].some((k) => Number(totals.value?.[k]) > 0))

const recipientFields = computed(() => {
  const fields: Array<{ key: string; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status' }
  ]
  if (campaign.value?.promo_template_id) {
    fields.push({ key: 'promo_code', label: 'Promo code' }, { key: 'promo_redeemed', label: 'Redeemed' })
  }
  fields.push({ key: 'error', label: 'Error' }, { key: 'sent_at', label: 'Sent' })
  return fields
})

// Five detail cards share the row when a campaign issues promo codes; four otherwise
const detailColClass = computed(() => (promo.value ? 'col-xl col-lg-4 col-md-6' : 'col-xl-3 col-lg-6'))
const promo = ref<{ issued: number; redeemed: number; expired: number } | null>(null)
function goToParent() {
  if (campaign.value?.parent_campaign_id) proxy.$router.push(`/marketing/campaigns/${campaign.value.parent_campaign_id}/report`)
}

function sendTimeDisplay(c: any): string {
  const hm = String(c.send_time || '09:00').slice(0, 5)
  const group = JSON.parse(localStorage.getItem('group') || '{}')
  const d = new Date(`2000-01-01T${hm}:00`)
  return Number.isNaN(d.getTime()) ? hm : formatGroupTime(d.toISOString()) || hm
}

const STATUS_BADGE_TYPES: Record<string, string> = {
  draft: 'default',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  cancelled: 'default',
  failed: 'danger'
}
const statusBadgeType = computed(() => STATUS_BADGE_TYPES[campaign.value?.status] || 'default')
// audience_filter arrives parsed; a visit lapse campaign anchors its codes on the send day
const isVisitCampaign = computed(() => Number.isInteger(campaign.value?.audience_filter?.days_since_last_visit?.days))

// The send/schedule time lives inside the status badge; header dates honor
// the group's show day of week setting (formatGroupDate default)
const statusBadgeLabel = computed(() => {
  const c = campaign.value
  if (!c) return ''
  if (c.recurrence === 'daily') {
    if (c.status === 'scheduled') return `active, runs daily at ${sendTimeDisplay(c)}`
    if (c.status === 'sent') return c.sent_at ? `finished ${formatGroupDate(c.sent_at)}` : 'finished'
    if (c.status === 'cancelled') return 'stopped'
  }
  if (c.status === 'sent' && c.sent_at) return `sent ${displayDateTime(c.sent_at)}`
  if (c.status === 'scheduled' && c.scheduled_at) return `scheduled for ${displayDateTime(c.scheduled_at)}`
  const hold = holdStatusLabel(c)
  if (hold) return hold
  return c.status
})

// Hold explanation (reputation gate or large list warm up) above the stat cards
const holdHint = computed(() => campaign.value
  ? holdHintText(campaign.value, Number(totals.value?.sent) || 0, Number(campaign.value.recipient_count) || 0, displayDateTime)
  : null)

function displayDateTime(value: string) {
  return `${formatGroupDate(value)}, ${formatGroupTime(value)}`
}

// Created row: full form with the weekday when it fits, shorter form without it otherwise
const createdDisplay = computed(() => (campaign.value?.created_at ? displayDateTime(campaign.value.created_at) : ''))
const createdShort = computed(() => (campaign.value?.created_at
  ? `${formatGroupDate(campaign.value.created_at, { dayOfWeek: false })}, ${formatGroupTime(campaign.value.created_at)}`
  : ''))

const recipientStatusColor = (status: string) => {
  if (status === 'sent') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'skipped') return 'default'
  return 'info'
}

function recipientName(r: any) {
  return `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email
}

// Mailchimp-style headline rates: opens and clicks over delivered
const openRate = computed(() => {
  const t = totals.value
  return t.delivered > 0 ? `${Math.round((t.unique_opens / t.delivered) * 100)}%` : '0%'
})
const clickRate = computed(() => {
  const t = totals.value
  return t.delivered > 0 ? `${Math.round(((t.unique_clicks || 0) / t.delivered) * 100)}%` : '0%'
})

const attributedRevenue = computed(() => {
  const gs = getGroupSettings()
  return formatCurrency(attribution.value.revenue, {
    currency: gs.currency,
    currencyFormat: gs.currency_format,
    numberFormat: gs.number_format
  })
})

function formatDateTime(value: string) {
  const date = formatGroupDate(value, { dayOfWeek: false })
  const time = formatGroupTime(value)
  return `${date}, ${time}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function recipientQueryFor(filters: Record<string, any>, count: number, offset: number) {
  let query = `count=${count}&offset=${offset}`
  if (filters.search) query += `&search=${encodeURIComponent(filters.search)}`
  if (filters.status && filters.status.length) {
    query += `&status=${filters.status.join(',')}`
  }
  if (filters.issue && filters.issue.length) query += `&issue=${filters.issue.join(',')}`
  if (filters.reason && filters.reason.length) {
    query += `&reason=${filters.reason.join(',')}`
  }
  return query
}

function recipientQuery(count: number, offset: number) {
  return recipientQueryFor(recipientFilters.value, count, offset)
}

async function fetchRecipients() {
  recipientsRefreshing.value = true
  try {
    const config = await authConfig()
    const offset = (currentPage.value - 1) * perPage.value
    const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/recipients?${recipientQuery(perPage.value, offset)}`
    const response = await axios.get(url, config)
    recipients.value = response.data.recipients || []
    recipientTotal.value = response.data.total_rows || 0
    recipientsScoped.value = response.data.scoped === true
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the recipient list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    recipientsRefreshing.value = false
  }
}

function onRecipientFiltersChanged(values: Record<string, any>) {
  recipientFilters.value = values
  currentPage.value = 1
  fetchRecipients()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchRecipients()
}

async function fetchStats(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/stats`
    const response = await axios.get(url, config)
    campaign.value = response.data.campaign
    totals.value = response.data.totals
    attribution.value = response.data.attribution || attribution.value
    promo.value = response.data.promo || null

    // Poll while sending so the report tracks progress live; a held campaign
    // changes nothing for hours, so back off to once a minute
    const wantedInterval = campaign.value.status === 'sending' ? (campaign.value.hold_reason ? 60000 : 10000) : 0
    if (pollTimer && (wantedInterval === 0 || wantedInterval !== pollIntervalMs)) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    if (wantedInterval > 0 && !pollTimer) {
      pollIntervalMs = wantedInterval
      pollTimer = setInterval(() => { fetchStats(false); fetchRecipients() }, wantedInterval)
    }
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the campaign report', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

function purchasesUrl(count: number, offset: number) {
  return `${baseUrl}/email-campaigns/${proxy.$route.params.id}/stats?detail=purchases&count=${count}&offset=${offset}`
}

async function fetchPurchases() {
  purchasesLoading.value = true
  try {
    const config = await authConfig()
    const offset = (purchasesPage.value - 1) * PURCHASES_PER_PAGE
    const response = await axios.get(purchasesUrl(PURCHASES_PER_PAGE, offset), config)
    purchases.value = response.data.purchases || []
    purchasesTotal.value = Number(response.data.total_rows) || 0
  } catch (err: any) {
    proxy.$notify({ type: 'danger', message: 'Could not load the purchases list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    purchasesLoading.value = false
  }
}

function openPurchasesDrawer() {
  purchasesDrawerOpen.value = true
  purchasesPage.value = 1
  fetchPurchases()
}

function onPurchasesPageChange(page: number) {
  purchasesPage.value = page
  fetchPurchases()
}

// Full list, not just the current page; same CSV conventions as downloadCsv
async function downloadPurchasesCsv() {
  purchasesExporting.value = true
  try {
    const config = await authConfig()
    const EXPORT_PAGE = 10000
    const rows: any[] = []
    let offset = 0
    let total = Infinity
    while (offset < total) {
      const response = await axios.get(purchasesUrl(EXPORT_PAGE, offset), config)
      const page = response.data.purchases || []
      rows.push(...page)
      total = Number(response.data.total_rows) || 0
      if (page.length < EXPORT_PAGE) break
      offset += EXPORT_PAGE
    }

    let csv = 'Customer,Order,Purchased,Paid,Refunded,Clicked\n'
    rows.forEach((r: any) => {
      csv += [
        escapeCsvValue(`${r.first_name || ''} ${r.last_name || ''}`.trim()),
        escapeCsvValue(r.order_number || ''),
        escapeCsvValue(r.timestamp ? formatDateTime(r.timestamp) : ''),
        r.paid,
        r.refunded,
        escapeCsvValue(r.first_click ? formatDateTime(r.first_click) : '')
      ].join(',')
      csv += '\n'
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `campaign-${proxy.$route.params.id}-purchases.csv`
    anchor.click()
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the purchases list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    purchasesExporting.value = false
  }
}

// Same CSV conventions as the transactions export: quote-escape, data URI
function escapeCsvValue(value: any) {
  if (typeof value === 'string') {
    value = value.replace(/"/g, '""')
    if (value.includes(',') || value.includes('\n')) {
      value = `"${value}"`
    }
  }
  return value ?? ''
}

// Exports the table's current filters by default; the Deliverability rows pass
// their own filter set and a file name suffix.
async function downloadCsv(override: Record<string, any> | null = null, suffix = 'recipients') {
  exporting.value = true
  try {
    const config = await authConfig()
    const filters = override ?? recipientFilters.value
    // The recipients endpoint serves at most 10,000 rows per request; page
    // through so a large campaign exports in full.
    const EXPORT_PAGE = 10000
    const rows: any[] = []
    let offset = 0
    let total = Infinity
    while (offset < total) {
      const url = `${baseUrl}/email-campaigns/${proxy.$route.params.id}/recipients?${recipientQueryFor(filters, EXPORT_PAGE, offset)}`
      const response = await axios.get(url, config)
      const page = response.data.recipients || []
      rows.push(...page)
      total = Number(response.data.total_rows) || 0
      if (page.length < EXPORT_PAGE) break
      offset += EXPORT_PAGE
    }

    const withPromo = !!campaign.value?.promo_template_id
    let csv = 'Name,Email,Status,Marked as spam,Bounced,Error,Sent' + (withPromo ? ',Promo code,Redeemed' : '') + '\n'
    rows.forEach((r: any) => {
      const cells = [
        escapeCsvValue(recipientName(r)),
        escapeCsvValue(r.email),
        r.status,
        r.complained ? 'yes' : 'no',
        r.bounced ? 'yes' : 'no',
        escapeCsvValue(r.error ? reasonLabel(r.error) : ''),
        escapeCsvValue(r.sent_at ? formatDateTime(r.sent_at) : '')
      ]
      if (withPromo) cells.push(escapeCsvValue(r.promo_code || ''), r.promo_redeemed ? 'yes' : 'no')
      csv += cells.join(',')
      csv += '\n'
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `campaign-${proxy.$route.params.id}-${suffix}.csv`
    anchor.click()
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the recipient list', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    exporting.value = false
  }
}

onMounted(() => { fetchStats(); fetchRecipients() })
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

function goToList() {
  proxy.$router.push('/marketing/campaigns')
}

function copyUtmToken() {
  navigator.clipboard.writeText(`campaign-${campaign.value.id}`)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}

return { __sfc: true,proxy, companyId, companyGroupId, baseUrl, loading, exporting, campaign, totals, attribution, purchasesDrawerOpen, purchases, purchasesTotal, purchasesLoading, purchasesPage, PURCHASES_PER_PAGE, purchasesExporting, recipients, recipientTotal, recipientsScoped, recipientsRefreshing, currentPage, perPage, recipientFilters, pollTimer, pollIntervalMs, REASON_OPTIONS, EMPTY_FILTERS, STAT_DOWNLOADS, downloadStat, recipientFilterSchema, storedPermissions, hasDownloadPermission, hasCustomerViewPermission, hasAnyStatDownload, recipientFields, detailColClass, promo, goToParent, sendTimeDisplay, STATUS_BADGE_TYPES, statusBadgeType, isVisitCampaign, statusBadgeLabel, holdHint, displayDateTime, createdDisplay, createdShort, recipientStatusColor, recipientName, openRate, clickRate, attributedRevenue, formatDateTime, authConfig, recipientQueryFor, recipientQuery, fetchRecipients, onRecipientFiltersChanged, onPerPageChange, fetchStats, purchasesUrl, fetchPurchases, openPurchasesDrawer, onPurchasesPageChange, downloadPurchasesCsv, escapeCsvValue, downloadCsv, goToList, copyUtmToken, BTable, BPagination, Badge, Breadcrumb, BreadcrumbItem, AttributedPurchasesDrawer, reasonLabel }
}

})